import React, { useState, useEffect, useCallback } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import {
  DragIndicator as DragHandleIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { translate as t } from 'hooks/useTranslations';
import {
  PolicyHeaderOrderItem,
  PolicyHeaderFieldType,
} from '../../../_types/manuals/draft';
import SortableHeaderOrderItem from './SortableHeaderOrderItem';
import HeaderLayoutPreview from './HeaderLayoutPreview';
import { useManualPropertiesContext } from '../context';

// Helper function to get display name for header types
const getHeaderDisplayName = (
  orderType: PolicyHeaderFieldType,
  customFields?: Array<{ header: string; value: string; }>
): string => {
  switch (orderType) {
    case 'EFFECTIVE_DATE':
      return t('draftManuals.manualProperties.policyHeaders.order.effectiveDate');
    case 'REVISED_DATE':
      return t('draftManuals.manualProperties.policyHeaders.order.revisedDate');
    case 'AGENCY_LOGO':
      return t('draftManuals.manualProperties.policyHeaders.order.agencyLogo');
    case 'ISSUING_AUTHORITY':
      return t('draftManuals.manualProperties.policyHeaders.order.issuingAuthority');
    case 'CUSTOM1':
      return customFields?.[0]?.header || t('draftManuals.manualProperties.policyHeaders.order.customField1');
    case 'CUSTOM2':
      return customFields?.[1]?.header || t('draftManuals.manualProperties.policyHeaders.order.customField2');
    default:
      return orderType;
  }
};

const HeaderOrderingSection: React.FC = () => {
  // Get all state and handlers from context
  const {
    headerOrder,
    setHeaderOrder,
    draftManual,
    dateFormat,
    issuingAuthorityType,
    agencyLogoId,
    issuingAuthoritySignatureId,
    issuingAuthorityName,
    customFields,
    localAttachments,
    handleUpdateAgencyLogo,
    handleUpdateIssuingAuthoritySignature,
    handleUpdateIssuingAuthorityName,
    handleUpdateCustomField,
    handleToggleVisibility,
    handleAttachmentUploaded,
  } = useManualPropertiesContext();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [localOrder, setLocalOrder] = useState<PolicyHeaderOrderItem[]>(headerOrder);

  // Update local order when prop changes
  useEffect(() => {
    setLocalOrder(headerOrder);
  }, [headerOrder]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;

      if (active.id !== over.id) {
        const oldIndex = localOrder.findIndex((item) => item.type === active.id);
        const newIndex = localOrder.findIndex((item) => item.type === over.id);
        const newOrder = arrayMove(localOrder, oldIndex, newIndex);
        setLocalOrder(newOrder);
        setHeaderOrder(newOrder);
      }
      setActiveId(null);
    },
    [localOrder, setHeaderOrder]
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleToggleVisibilityLocal = useCallback(
    (orderType: PolicyHeaderFieldType) => {
      // Use toggle handler from context
      handleToggleVisibility(orderType);
    },
    [handleToggleVisibility]
  );

  const activeItem = activeId ? localOrder.find((item) => item.type === activeId) : null;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ mt: 7, mb: 2 }}>
        {t('draftManuals.manualProperties.policyHeaders.order.title')}
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        {t(
          'draftManuals.manualProperties.policyHeaders.order.description'
        )}
      </Typography>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={localOrder.map((item) => item.type)} strategy={verticalListSortingStrategy}>
          <Stack spacing={2}>
            {localOrder.map((item) => (
              <SortableHeaderOrderItem
                key={item.type}
                item={item}
                displayName={getHeaderDisplayName(item.type, customFields ?? draftManual.headerConfiguration?.customFields)}
                onToggleVisibility={handleToggleVisibilityLocal}
                manualId={draftManual.id}
                issuingAuthorityType={issuingAuthorityType}
                agencyLogoId={agencyLogoId}
                issuingAuthoritySignatureId={issuingAuthoritySignatureId}
                issuingAuthorityName={issuingAuthorityName}
                customFields={customFields}
                attachments={localAttachments}
                onUpdateAgencyLogo={handleUpdateAgencyLogo}
                onUpdateIssuingAuthoritySignature={handleUpdateIssuingAuthoritySignature}
                onUpdateIssuingAuthorityName={handleUpdateIssuingAuthorityName}
                onUpdateCustomField={handleUpdateCustomField}
                onAttachmentUploaded={handleAttachmentUploaded}
              />
            ))}
          </Stack>
        </SortableContext>
        <DragOverlay>
          {activeItem ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'background.paper',
                boxShadow: 2,
                opacity: 0.9,
              }}
            >
              <IconButton size="small" sx={{ cursor: 'grabbing' }}>
                <DragHandleIcon />
              </IconButton>
              <Typography variant="body1" sx={{ flex: 1 }}>
                {getHeaderDisplayName(activeItem.type, customFields ?? draftManual.headerConfiguration?.customFields)}
              </Typography>
              <IconButton size="small" color={activeItem.state === 'ENABLED' ? 'primary' : 'default'}>
                {activeItem.state === 'ENABLED' ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </IconButton>
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Real-time Preview */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ mt: 7, mb: 2 }}>
          {t('draftManuals.manualProperties.policyHeaders.order.preview')}
        </Typography>
        <HeaderLayoutPreview
          order={localOrder}
          headerConfig={draftManual.headerConfiguration}
          dateFormat={dateFormat}
          issuingAuthorityType={issuingAuthorityType}
          agencyLogoId={agencyLogoId}
          issuingAuthoritySignatureId={issuingAuthoritySignatureId}
          issuingAuthorityName={issuingAuthorityName}
          manualId={draftManual.id}
          customFields={customFields}
        />
      </Box>
    </Box>
  );
};

export default HeaderOrderingSection;